import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, Input, OnInit, signal, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BxSpinnerModule } from '@buildxact/ui-components/bx-spinner';
import { NgSelectModule } from '@ng-select/ng-select';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { catchError, combineLatest, concatMap, EMPTY, finalize, first, tap } from 'rxjs';
import { CreateTimesheetCategoryInput, TimesheetCategory, TimesheetSyncProvider } from '../../../../../../graphql';
import { BxLogger } from '../../../../../core/services/bx-logger.service';
import { JobCategorySlimModel } from '../../../../../data/models/job.model';
import { JobItemsService } from '../../../../../data/repositories/jobs/job-items.service';
import { TimesheetApiService } from '../../../../../shared/timesheets/domain/api/timesheet.api.service';
import { TimesheetJob } from '../import-timesheets/import-timesheets.component';

@Component({
    selector: 'app-job-categories',
    imports: [CommonModule, FormsModule, NgSelectModule, BxSpinnerModule],
    templateUrl: './job-categories-component.html',
    styleUrls: ['./job-categories-component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobCategoriesComponent implements OnInit {
    @Input() selectedJob: TimesheetJob | null = null;
    @Input() timesheetProvider: TimesheetSyncProvider | null = null;
    @Input() isModal: boolean = false;
    action = new EventEmitter<number>();
    isLoading = signal(false);
    providerCategories = signal<TimesheetCategory[]>([]);
    jobCategories = signal<JobCategorySlimModel[]>([]);

    #jobItemsService = inject(JobItemsService);
    #logger = inject(BxLogger);
    #timesheetService = inject(TimesheetApiService);
    #modalRef = inject(BsModalRef);

    ngOnInit() {
        // When we open via Modal, we need to load the categories here
        if (this.selectedJob && this.timesheetProvider) {
            this.providerCategories.set([]);
            this.jobCategories.set([]);
            this.#loadCategories();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        // TODO: We need to revisit this and do it differently. Maybe using signals or stores.
        if (
            (this.selectedJob || (changes.selectedJob && changes.selectedJob.currentValue)) &&
            (this.timesheetProvider || (changes.timesheetProvider && changes.timesheetProvider.currentValue))
        ) {
            this.providerCategories.set([]);
            this.jobCategories.set([]);
            this.#loadCategories();
        } else {
            this.providerCategories.set([]);
            this.jobCategories.set([]);
        }
    }

    availableCategories = computed(() => {
        return this.providerCategories().filter((category) => {
            return !this.jobCategories().some(
                (jobCategory) => jobCategory.timesheetCategoryId && jobCategory.timesheetCategoryId === category.id
            );
        });
    });

    #loadCategories() {
        if (this.selectedJob && this.timesheetProvider) {
            this.isLoading.set(true);

            const job = this.selectedJob!;
            combineLatest([
                this.#jobItemsService.getJobItemsForJobId(job.iD_Job, 500, 0),
                this.#timesheetService.getTimesheetCategories({
                    jobId: job.iD_Job,
                    timesheetProvider: this.timesheetProvider!,
                }),
            ])
                .pipe(
                    first(),
                    catchError(() => {
                        this.#logger.error('Failed to load job categories');
                        return EMPTY;
                    }),
                    finalize(() => this.isLoading.set(false))
                )
                .subscribe(([jobItems, { data }]) => {
                    this.jobCategories.set(jobItems?.data?.jobItemsByJobId?.items ?? []);
                    this.providerCategories.set(data?.timesheetCategories ?? []);

                    this.isLoading.set(false);
                });
        }
    }

    createArea(selectedItem: JobCategorySlimModel) {
        this.isLoading.set(true);

        const createCategoryPayload = {
            timesheetProvider: this.timesheetProvider,
            data: [
                { key: 'company', value: this.selectedJob?.timesheetLocationId },
                { key: 'operationalUnitName', value: selectedItem.description },
            ],
        } as CreateTimesheetCategoryInput;

        this.#timesheetService
            .createTimesheetCategory(createCategoryPayload)
            .pipe(
                first(),
                catchError(() => {
                    this.#logger.error('Error adding area. Area may already exist.');
                    this.isLoading.set(false);
                    return EMPTY;
                }),
                concatMap((result) => {
                    if (result.errors?.length || result.data?.createTimesheetCategory?.errors?.length) {
                        this.#logger.error('Error adding area. Area may already exist.');
                        this.isLoading.set(false);
                        return EMPTY;
                    }

                    const categoryId = result?.data?.createTimesheetCategory?.timesheetCategory?.id;
                    if (categoryId) {
                        this.#logger.success('Area successfully created.');
                        const category = result.data!.createTimesheetCategory.timesheetCategory!;
                        if (!this.providerCategories().some((c) => c.id === category.id)) {
                            this.providerCategories.set([...this.providerCategories(), category]);
                        }
                        return this.#setAreaIdInternal(selectedItem, category);
                    }
                    this.isLoading.set(false);
                    return EMPTY;
                })
            )
            .subscribe((result) => {
                this.isLoading.set(false);
            });
    }

    setAreaId(jobItem: JobCategorySlimModel, option?: TimesheetCategory) {
        if (!option) return;
        if (!jobItem.timesheetCategoryId) return;

        this.#setAreaIdInternal(jobItem, option)
            .pipe(first())
            .subscribe((result) => {
                this.isLoading.set(false);
            });
    }

    #setAreaIdInternal(jobItem: JobCategorySlimModel, timesheetCategory: TimesheetCategory) {
        this.isLoading.set(true);
        return this.#timesheetService
            .linkJobItemToTimesheetCategory({
                jobId: this.selectedJob!.iD_Job,
                jobItemId: jobItem.iD_JobItem!,
                timesheetProvider: this.timesheetProvider!,
                timesheetCategoryId: timesheetCategory.id,
            })
            .pipe(
                first(),
                tap(({ data }) => {
                    if (data?.linkJobItemToTimesheetCategory?.boolean) {
                        this.jobCategories.update((currentJobCategories) => {
                            return currentJobCategories.map((jobCategory) => {
                                if (jobCategory.iD_JobItem !== jobItem.iD_JobItem) {
                                    return jobCategory;
                                }
                                return {
                                    ...jobCategory,
                                    timesheetCategoryId: timesheetCategory.id,
                                };
                            });
                        });
                        this.#logger.success('Area linked.');
                    } else {
                        this.#logger.error('Failed to link area');
                    }

                    this.isLoading.set(false);
                }),
                catchError((e) => {
                    this.#logger.error('Failed to link area');
                    return EMPTY;
                })
            );
    }

    unlinkArea(selectedItem: JobCategorySlimModel) {
        this.isLoading.set(true);
        this.#timesheetService
            .unlinkJobItemFromTimesheetCategory({
                jobId: this.selectedJob!.iD_Job,
                jobItemId: selectedItem.iD_JobItem!,
                timesheetProvider: this.timesheetProvider!,
            })
            .pipe(
                first(),
                catchError(() => {
                    this.#logger.error('Failed to unlink area');
                    this.isLoading.set(false);
                    return EMPTY;
                })
            )
            .subscribe((response) => {
                if (response.data?.unlinkJobItemFromTimesheetCategory.boolean) {
                    this.jobCategories.update((currentJobCategories) => {
                        return currentJobCategories.map((jobCategory) => {
                            if (jobCategory.iD_JobItem !== selectedItem.iD_JobItem) {
                                return jobCategory;
                            }
                            return {
                                ...jobCategory,
                                timesheetCategoryId: null,
                            };
                        });
                    });
                    this.#logger.success('Area unlinked');
                } else {
                    this.#logger.error('Failed to unlink area');
                }

                this.isLoading.set(false);
            });
    }

    closeModal() {
        const linkedCategoriesCount =
            this.jobCategories().filter((i) => {
                return i.timesheetCategoryId?.length;
            })?.length ?? 0;
        this.action.emit(linkedCategoriesCount);
        this.#modalRef.hide();
    }
}
